import { Badge } from '@/components/ui/badge';
import { ResponsiveTable, type ResponsiveColumn } from '@/components/ui/responsive-table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { PmAccessLogEntry, PmVisitStatus } from '@/types';

const STATUS_VARIANTS: Record<PmVisitStatus, 'default' | 'secondary' | 'destructive' | 'outline'> =
  {
    granted: 'default',
    checked_out: 'secondary',
    denied: 'destructive',
    expired: 'outline',
    pending: 'secondary',
  };

const STATUS_LABELS: Record<PmVisitStatus, string> = {
  granted: 'On Property',
  checked_out: 'Checked Out',
  denied: 'Denied',
  expired: 'Expired',
  pending: 'Pending',
};

const TYPE_LABELS: Record<string, string> = {
  single_visit: 'Single Visit',
  recurring: 'Recurring',
  domestic_worker: 'Domestic',
  delivery: 'Delivery',
  taxi: 'Taxi',
  event: 'Event',
};

interface AccessLogTableProps {
  entries: PmAccessLogEntry[];
  loading?: boolean;
  onRowClick: (entry: PmAccessLogEntry) => void;
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(entry: PmAccessLogEntry): string {
  if (entry.durationMinutes) {
    return entry.durationMinutes >= 60
      ? `${Math.floor(entry.durationMinutes / 60)}h ${entry.durationMinutes % 60}m`
      : `${entry.durationMinutes}m`;
  }
  return entry.status === 'granted' ? 'On property' : '—';
}

function formatPersons(entry: PmAccessLogEntry): string {
  if ((entry.actualPartySize ?? 1) <= 1) return '—';
  return entry.actualPartySize !== entry.expectedPartySize
    ? `${entry.actualPartySize} / ${entry.expectedPartySize}`
    : `${entry.actualPartySize}`;
}

/**
 * Columns shared by both presentations. `primary` columns (Time, Status,
 * Visitor) form the card header on phones; the rest collapse into the card body
 * instead of forcing a horizontal scroll (NFR-06).
 */
const COLUMNS: ResponsiveColumn<PmAccessLogEntry>[] = [
  {
    key: 'time',
    header: 'Time',
    priority: 'primary',
    className: 'w-36 text-sm tabular-nums',
    cell: (e) => <span className="text-sm tabular-nums">{formatTime(e.timestamp)}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    priority: 'primary',
    className: 'w-28',
    cell: (e) => (
      <Badge
        variant={STATUS_VARIANTS[e.status]}
        className="text-xs uppercase tracking-widest font-mono"
      >
        {STATUS_LABELS[e.status]}
      </Badge>
    ),
  },
  {
    key: 'visitor',
    header: 'Visitor',
    priority: 'primary',
    cell: (e) => <span className="font-medium">{e.visitorName}</span>,
  },
  {
    key: 'persons',
    header: 'Persons',
    priority: 'secondary',
    className: 'w-20 text-sm tabular-nums',
    cell: (e) => formatPersons(e),
  },
  {
    key: 'host',
    header: 'Host / Unit',
    priority: 'secondary',
    cell: (e) => [e.hostName, e.hostUnit].filter(Boolean).join(' / ') || '—',
  },
  {
    key: 'gate',
    header: 'Gate',
    priority: 'secondary',
    cell: (e) => e.gateName,
  },
  {
    key: 'type',
    header: 'Type',
    priority: 'secondary',
    cell: (e) => (
      <Badge variant="outline" className="text-xs">
        {TYPE_LABELS[e.invitationType] ?? e.invitationType}
      </Badge>
    ),
  },
  {
    key: 'duration',
    header: 'Duration',
    priority: 'secondary',
    className: 'tabular-nums text-sm',
    cell: (e) => <span className="tabular-nums text-sm">{formatDuration(e)}</span>,
  },
];

function LoadingTable() {
  return (
    <div className="rounded-md border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableHead key={col.key} className={col.className}>
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 8 }).map((_, i) => (
            <TableRow key={i}>
              {COLUMNS.map((col) => (
                <TableCell key={col.key}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function AccessLogTable({ entries, loading, onRowClick }: AccessLogTableProps) {
  if (loading) return <LoadingTable />;

  return (
    <ResponsiveTable
      columns={COLUMNS}
      rows={entries}
      getRowKey={(e) => e.id}
      onRowClick={onRowClick}
      rowLabel={(e) => `View visit detail for ${e.visitorName}`}
      caption="Access events"
    />
  );
}
